import { safeRedirectPath } from "@/lib/errors";

const STORAGE_KEY = "memoru_extension_connect";
const PERSISTENT_STORAGE_KEY = "memoru_extension_connect_paired";
const CONNECT_PATH = "/auth/connect-extension";
const EXTENSION_SIGN_IN_PATH = "/auth/extension-sign-in";
const UUIDISH_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EXTENSION_ID_PATTERN = /^[a-p]{32}$/;
const EXTENSION_IDS: Record<string, string[]> = {
  development: ["iomlnpaoigbckioplgcpfgmbkcbfooak"],
  staging: ["jglmcgbeimlelofbgdlmmokciimmmcon"],
  production: (import.meta.env.VITE_PRODUCTION_EXTENSION_IDS ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter((id) => EXTENSION_ID_PATTERN.test(id)),
};

function isConfiguredExtensionId(extensionId: string): boolean {
  const mode = import.meta.env.MODE === "test" ? "development" : import.meta.env.MODE;
  return EXTENSION_IDS[mode]?.includes(extensionId) ?? false;
}

export interface ExtensionConnectContext {
  clientId: string;
  extensionId: string;
  redirect: string;
}

interface ExternalMessageResponse {
  ok?: boolean;
  signedIn?: boolean;
  email?: string;
  error?: string;
  accountId?: string;
  workspaceId?: string;
  found?: boolean;
}

export interface ExtensionAuthState {
  accountId: string | null;
  setupState: "ready" | "grant_access";
  authRevision: number;
  pendingSignOut: boolean;
}

interface ExtensionAuthPort {
  postMessage(message: Record<string, unknown>): void;
  disconnect(): void;
  onMessage: { addListener(listener: (message: unknown) => void): void; removeListener(listener: (message: unknown) => void): void };
  onDisconnect: { addListener(listener: () => void): void };
}

interface ChromeExternalRuntime {
  connect?(extensionId: string, options: { name: string }): ExtensionAuthPort;
  sendMessage(
    extensionId: string,
    message:
      | {
          type: "MEMORU_CONNECT_CODE";
          version: 1;
          clientId: string;
          connectCode: string;
          requestId: string;
        }
      | {
          type: "MEMORU_OPEN_PERMISSIONS";
          clientId: string;
        }
        | {
          type: "MEMORU_SIGN_OUT";
          version: 1;
          clientId: string;
          requestId: string;
        }
      | {
          type: "MEMORU_GET_STATE";
          clientId: string;
        }
      | {
          type: "MEMORU_RECORDING_RETRY";
          schema_version: 1;
          meeting_id: string;
        },
  ): Promise<ExternalMessageResponse | undefined>;
}

declare global {
  var chrome: { runtime?: ChromeExternalRuntime } | undefined;
}

export function isValidClientId(value: string): boolean {
  return UUIDISH_PATTERN.test(value.trim());
}

export function isValidExtensionId(value: string): boolean {
  return EXTENSION_ID_PATTERN.test(value.trim());
}

function normalizeContext(value: unknown): ExtensionConnectContext | null {
  if (!value || typeof value !== "object") return null;

  const candidate = value as Record<string, unknown>;
  const clientId = typeof candidate.clientId === "string" ? candidate.clientId.trim() : "";
  const extensionId =
    typeof candidate.extensionId === "string" ? candidate.extensionId.trim() : "";
  const redirect =
    typeof candidate.redirect === "string"
      ? safeRedirectPath(candidate.redirect, CONNECT_PATH)
      : CONNECT_PATH;

  if (!isValidClientId(clientId) || !isValidExtensionId(extensionId)) return null;
  if (!isConfiguredExtensionId(extensionId)) return null;
  return { clientId, extensionId, redirect };
}

export function saveExtensionConnectContext(context: ExtensionConnectContext): boolean {
  const normalized = normalizeContext(context);
  if (!normalized) return false;

  const payload = JSON.stringify(normalized);
  try {
    sessionStorage.setItem(STORAGE_KEY, payload);
    localStorage.setItem(PERSISTENT_STORAGE_KEY, payload);
    window.dispatchEvent(new Event("memoru-extension-context"));
    return true;
  } catch {
    return false;
  }
}

function readStoredContext(raw: string | null): ExtensionConnectContext | null {
  if (!raw) return null;
  try {
    const context = normalizeContext(JSON.parse(raw));
    if (!context) return null;
    return context;
  } catch {
    return null;
  }
}

export function getExtensionConnectContext(): ExtensionConnectContext | null {
  try {
    const fromSession = readStoredContext(sessionStorage.getItem(STORAGE_KEY));
    if (fromSession) return fromSession;

    const fromLocal = readStoredContext(localStorage.getItem(PERSISTENT_STORAGE_KEY));
    if (fromLocal) {
      // Rehydrate session tab storage when the user returns in a new tab.
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromLocal));
      } catch {
        // localStorage pairing is enough for sign-out messaging.
      }
      return fromLocal;
    }

    return null;
  } catch {
    return null;
  }
}

function contextFromParams(params: URLSearchParams): ExtensionConnectContext | null {
  const clientId = params.get("client_id")?.trim() ?? "";
  const extensionId = params.get("extension_id")?.trim() ?? "";
  if (!clientId && !extensionId) return null;

  return normalizeContext({
    clientId,
    extensionId,
    redirect: safeRedirectPath(params.get("redirect"), CONNECT_PATH),
  });
}

function contextFromRedirect(redirect: string | null): ExtensionConnectContext | null {
  const safeRedirect = safeRedirectPath(redirect, "");
  if (!safeRedirect) return null;

  const url = new URL(safeRedirect, "https://app.memoru.invalid");
  const context = contextFromParams(url.searchParams);
  return context ? { ...context, redirect: safeRedirect } : null;
}

export function captureExtensionConnectContext(search: string): ExtensionConnectContext | null {
  const params = new URLSearchParams(search);
  const hasDirectContext = params.has("client_id") || params.has("extension_id");
  let context: ExtensionConnectContext | null;

  if (hasDirectContext) {
    context = contextFromParams(params);
  } else {
    const safeRedirect = safeRedirectPath(params.get("redirect"), "");
    const redirectParams = safeRedirect
      ? new URL(safeRedirect, "https://app.memoru.invalid").searchParams
      : null;
    const hasRedirectContext =
      redirectParams?.has("client_id") || redirectParams?.has("extension_id");
    context = hasRedirectContext
      ? contextFromRedirect(safeRedirect)
      : getExtensionConnectContext();
  }

  if (context) saveExtensionConnectContext(context);
  return context;
}

function addContextToPath(path: string, context: ExtensionConnectContext): string {
  const url = new URL(safeRedirectPath(path, CONNECT_PATH), "https://app.memoru.invalid");
  url.searchParams.set("client_id", context.clientId);
  url.searchParams.set("extension_id", context.extensionId);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function extensionConnectPath(context = getExtensionConnectContext()): string {
  return context ? addContextToPath(CONNECT_PATH, context) : CONNECT_PATH;
}

export function isExtensionConnectRedirect(path: string): boolean {
  const safePath = safeRedirectPath(path, "");
  if (!safePath) return false;
  try {
    return new URL(safePath, "https://app.memoru.invalid").pathname === CONNECT_PATH;
  } catch {
    return false;
  }
}

export function isExtensionSignInRedirect(path: string): boolean {
  const safePath = safeRedirectPath(path, "");
  if (!safePath) return false;
  try {
    const url = new URL(safePath, "https://app.memoru.invalid");
    return url.pathname === EXTENSION_SIGN_IN_PATH;
  } catch {
    return false;
  }
}

export function parseExtensionSignInRedirect(path: string): ExtensionConnectContext | null {
  if (!isExtensionSignInRedirect(path)) return null;
  try {
    const url = new URL(safeRedirectPath(path, EXTENSION_SIGN_IN_PATH), "https://app.memoru.invalid");
    return normalizeContext({
      clientId: url.searchParams.get("client_id"),
      extensionId: url.searchParams.get("extension_id"),
      redirect: EXTENSION_SIGN_IN_PATH,
    });
  } catch {
    return null;
  }
}

export function buildExtensionSignInRedirect(clientId: string, extensionId: string): string {
  if (!isValidClientId(clientId) || !isValidExtensionId(extensionId)) {
    throw new Error("Extension sign-in details are invalid.");
  }
  const params = new URLSearchParams({
    client_id: clientId.trim(),
    extension_id: extensionId.trim(),
  });
  return `${EXTENSION_SIGN_IN_PATH}?${params.toString()}`;
}

export function extensionPermissionsUrl(
  extensionId: string,
  options: { clientId?: string; step?: string } = {},
): string {
  if (!isValidExtensionId(extensionId)) {
    throw new Error("Extension ID is invalid.");
  }

  const params = new URLSearchParams();
  if (options.clientId && isValidClientId(options.clientId)) {
    params.set("client_id", options.clientId.trim());
  }
  if (options.step) params.set("s", options.step);

  const query = params.toString();
  return `chrome-extension://${extensionId}/options.html${query ? `?${query}` : ""}#/Permissions`;
}

export async function openExtensionPermissions(
  extensionId: string,
  clientId: string,
): Promise<void> {
  if (!isValidExtensionId(extensionId) || !isValidClientId(clientId)) {
    throw new Error("Extension setup details are invalid.");
  }

  const runtime = globalThis.chrome?.runtime;
  if (!runtime?.sendMessage) {
    throw new Error("Chrome extension messaging is unavailable.");
  }

  let response: ExternalMessageResponse | undefined;
  try {
    response = await runtime.sendMessage(extensionId, {
      type: "MEMORU_OPEN_PERMISSIONS",
      clientId,
    });
  } catch {
    throw new Error("The memoru extension did not respond. Enable it, then try again.");
  }

  if (response?.ok !== true) {
    throw new Error(
      typeof response?.error === "string" && response.error.trim()
        ? response.error
        : "The extension could not open setup.",
    );
  }
}

export function postLoginRedirect(search: string, fallback = "/"): string {
  const params = new URLSearchParams(search);
  return safeRedirectPath(params.get("redirect"), fallback);
}

export function loginPathForRedirect(redirect: string): string {
  const safeRedirect = safeRedirectPath(redirect);
  const destination = new URL(safeRedirect, "https://app.memoru.invalid");
  const context =
    contextFromParams(destination.searchParams) ?? getExtensionConnectContext();
  const params = new URLSearchParams({ redirect: safeRedirect });

  if (context) {
    params.set("client_id", context.clientId);
    params.set("extension_id", context.extensionId);
  }
  return `/login?${params.toString()}`;
}

export async function sendConnectCodeToExtension(
  extensionId: string,
  clientId: string,
  connectCode: string,
): Promise<void> {
  if (!isValidExtensionId(extensionId) || !isValidClientId(clientId) || !connectCode.trim()) {
    throw new Error("The extension pairing details are invalid. Start again from the extension.");
  }

  const runtime = globalThis.chrome?.runtime;
  if (!runtime?.sendMessage) {
    throw new Error("The memoru extension is not available. Install or enable it, then try again.");
  }

  let response: ExternalMessageResponse | undefined;
  try {
    response = await runtime.sendMessage(extensionId, {
      type: "MEMORU_CONNECT_CODE",
      version: 1,
      clientId,
      connectCode,
      requestId: crypto.randomUUID(),
    });
  } catch {
    throw new Error("The memoru extension did not respond. Enable it, then try again.");
  }

  if (response?.ok !== true || response.signedIn !== true) {
    throw new Error(
      typeof response?.error === "string" && response.error.trim()
        ? response.error
        : "The extension could not finish signing in. Try again.",
    );
  }
}

/**
 * Completes the one-account sign-in flow. The web session is the only user
 * sign-in; it mints a short-lived, client-bound code for the extension. The
 * extension never receives or reads the web HttpOnly refresh cookie.
 */
export async function pairExtension(
  context: ExtensionConnectContext,
  createConnectCode: (clientId: string, extensionId: string, workspaceId: string) => Promise<{ connect_code: string }>,
  workspaceId: string,
): Promise<void> {
  if (!isValidExtensionId(context.extensionId) || !isValidClientId(context.clientId)) {
    throw new Error("Extension sign-in details are invalid.");
  }
  if (!workspaceId.trim()) {
    throw new Error("Choose a workspace before connecting the extension.");
  }

  saveExtensionConnectContext(context);
  const { connect_code } = await createConnectCode(context.clientId, context.extensionId, workspaceId);
  await sendConnectCodeToExtension(context.extensionId, context.clientId, connect_code);
}

export interface ConnectedExtensionState {
  context: ExtensionConnectContext;
  signedIn: boolean;
  email: string | null;
  accountId: string | null;
  workspaceId: string | null;
}

export async function getConnectedExtensionState(): Promise<ConnectedExtensionState | null> {
  const context = getExtensionConnectContext();
  const runtime = globalThis.chrome?.runtime;
  if (!context || !runtime?.sendMessage) return null;

  let response: ExternalMessageResponse | undefined;
  try {
    response = await runtime.sendMessage(context.extensionId, {
      type: "MEMORU_GET_STATE",
      clientId: context.clientId,
    });
  } catch {
    return null;
  }

  if (response?.ok !== true) return null;
  const email = typeof response.email === "string" ? response.email.trim() : "";
  const accountId = typeof response.accountId === "string" ? response.accountId.trim() : "";
  const workspaceId = typeof response.workspaceId === "string" ? response.workspaceId.trim() : "";
  return { context, signedIn: response.signedIn === true, email: email || null, accountId: accountId || null, workspaceId: workspaceId || null };
}

export async function signOutConnectedExtension(): Promise<void> {
  const context = getExtensionConnectContext();
  const runtime = globalThis.chrome?.runtime;
  if (!context || !runtime?.sendMessage) return;

  try {
    await runtime.sendMessage(context.extensionId, {
      type: "MEMORU_SIGN_OUT",
      version: 1,
      clientId: context.clientId,
      requestId: crypto.randomUUID(),
    });
  } catch {
    // Web sign-out must still complete when the extension is unavailable.
  }
}

export function watchExtensionAuthState(
  listener: (state: ExtensionAuthState) => void,
): () => void {
  const context = getExtensionConnectContext();
  const runtime = globalThis.chrome?.runtime;
  if (!context || !runtime?.connect) return () => undefined;
  let port: ExtensionAuthPort;
  try {
    port = runtime.connect(context.extensionId, { name: "memoru-auth-v1" });
  } catch {
    return () => undefined;
  }
  const onMessage = (message: unknown) => {
    if (!message || typeof message !== "object") return;
    const value = message as Record<string, unknown>;
    if (value.type !== "STATE_CHANGED" || value.version !== 1) return;
    const revision = Number(value.authRevision);
    if (!Number.isSafeInteger(revision) || revision < 0) return;
    listener({
      accountId: typeof value.accountId === "string" ? value.accountId : null,
      setupState: value.setupState === "grant_access" ? "grant_access" : "ready",
      authRevision: revision,
      pendingSignOut: value.pendingSignOut === true,
    });
  };
  port.onMessage.addListener(onMessage);
  port.postMessage({ type: "GET_CONTEXT", version: 1, clientId: context.clientId });
  return () => {
    port.onMessage.removeListener(onMessage);
    port.disconnect();
  };
}

const RECORDING_RETRY_TIMEOUT_MS = 3000;

/**
 * Asks the paired extension to upload a meeting's audio now (contract C3).
 * `meetingId` is the server meeting id. Resolves `found: true` only when the
 * extension has local audio for it under the signed-in account and has started
 * uploading. A missing or silent extension (another computer, not installed,
 * never paired) is `found: false`; this never throws.
 */
export async function requestRecordingRetry(meetingId: string): Promise<{ found: boolean }> {
  const context = getExtensionConnectContext();
  const runtime = globalThis.chrome?.runtime;
  if (!context || !runtime?.sendMessage || !meetingId) return { found: false };
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const response = await Promise.race([
      runtime.sendMessage(context.extensionId, { type: "MEMORU_RECORDING_RETRY", schema_version: 1, meeting_id: meetingId }),
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), RECORDING_RETRY_TIMEOUT_MS);
      }),
    ]);
    return { found: response?.ok === true && response.found === true };
  } catch {
    return { found: false };
  } finally {
    if (timer) clearTimeout(timer);
  }
}
